import {
  createFileRoute,
  Navigate,
  Outlet,
} from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { Trans } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import {
  clearImpersonationEmail,
  getImpersonationConcept,
  getImpersonationEmail,
  getImpersonationOrganization,
} from "@/lib/auth/impersonation";

export const Route = createFileRoute("/(protected)/_layout")({
  component: ProtectedLayout,
});

function resolveLoginRedirectTarget(rawPath: string): string {
  const maxDepth = 12;
  let candidate = rawPath;

  for (let i = 0; i < maxDepth; i += 1) {
    if (!candidate.startsWith("/") || candidate.startsWith("//")) {
      return "/dashboard";
    }

    if (candidate === "/login") {
      return "/dashboard";
    }

    if (candidate.startsWith("/login?")) {
      const params = new URLSearchParams(candidate.slice("/login?".length));
      const nested = params.get("redirect");
      if (!nested) {
        return "/dashboard";
      }
      candidate = nested;
      continue;
    }

    return candidate;
  }

  return "/dashboard";
}

function ProtectedLayout() {
  const { isAuthenticated, isLoading, refetchUser, user } = useAuth();
  const impersonatedEmail = getImpersonationEmail();
  const impersonatedRole = user?.roles?.[0]?.name || user?.roles?.[0]?.code || "-";
  const impersonatedOrganization =
    user?.organization?.name ||
    getImpersonationOrganization() ||
    "-";
  const impersonatedConcept =
    user?.company?.name ||
    getImpersonationConcept() ||
    "-";

  const stopImpersonation = () => {
    clearImpersonationEmail();

    const url = new URL(window.location.href);
    url.searchParams.delete("loggedin_as");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);

    void refetchUser();
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="text-(--color-text-secondary)">
          <Trans>Loading...</Trans>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const requestedPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const redirectTarget = resolveLoginRedirectTarget(requestedPath);

    return (
      <Navigate
        to="/login"
        search={{ redirect: redirectTarget }}
        replace
      />
    );
  }

  // Every account gets a placeholder org at sign-up, so gate on whether that org
  // has been configured (setupCompleted) rather than on its mere existence.
  // Gate on an explicit `false` (the placeholder org created at sign-up) — a
  // missing/undefined flag must never trap a configured user in onboarding.
  const isSuperAdmin = user?.permissions?.includes("*:*") ?? false;
  const orgNeedsSetup = !!user?.organization?.id && user.organization.setupCompleted === false;
  if (!isSuperAdmin && (!user?.organization?.id || orgNeedsSetup)) {
    return <Navigate to="/onboarding/organization" replace />;
  }

  return (
    <div className="flex flex-1 flex-col">
      {impersonatedEmail ? (
        <div className="flex items-center justify-between gap-3 border-b border-(--color-warning)/30 bg-(--color-warning-bg) px-3 py-2 text-sm text-(--color-warning-text)">
          <div>
            <Trans>Impersonating:</Trans> {impersonatedEmail} | <Trans>Role:</Trans>{" "}
            {impersonatedRole} | <Trans>Organization:</Trans> {impersonatedOrganization} | <Trans>Concept:</Trans>{" "}
            {impersonatedConcept}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-(--color-warning)/40 bg-(--color-surface-page)/90 text-(--color-warning-text) hover:bg-(--color-surface-page)"
            onClick={stopImpersonation}
          >
            <Trans>Stop impersonation</Trans>
          </Button>
        </div>
      ) : null}
      <Outlet />
    </div>
  );
}
